import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { Input } from '@ui/components/Input'

import { Box } from '@shared/components/Box'

interface VerificationCodeInputProps {
  length?: number
  value: string
  onChange: (code: string) => void
  onComplete?: (code: string) => void
  error?: boolean
  disabled?: boolean
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = (props) => {
  const { length = 6, value, onChange, onComplete, error = false, disabled = false } = props

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const isCompleteCalledRef = useRef(false)

  const digits = useMemo(
    () => Array.from({ length }, (_, index) => value[index] || ''),
    [value, length]
  )

  const isCodeComplete = useMemo(() => {
    const code = digits.join('')
    return code.length === length && /^\d+$/.test(code)
  }, [digits, length])

  const focusField = useCallback(
    (index: number) => {
      if (index >= 0 && index < length) {
        requestAnimationFrame(() => {
          inputRefs.current[index]?.focus()
        })
      }
    },
    [length]
  )

  const updateCode = useCallback(
    (newDigits: string[]) => {
      onChange(newDigits.join(''))
    },
    [onChange]
  )

  const handleChange = useCallback(
    (index: number, newValue: string) => {
      if (disabled) return

      const digit = newValue.replace(/\D/g, '').slice(-1)

      const newDigits = digits.map((d, i) => (i === index ? digit : d))

      updateCode(newDigits)

      if (digit && index < length - 1) {
        focusField(index + 1)
      }
    },
    [digits, disabled, length, updateCode, focusField]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (disabled) return

      const { key } = e

      if (['e', 'E', '+', '-', '.', ','].includes(key)) {
        e.preventDefault()
        return
      }

      if (key === 'Backspace') {
        e.preventDefault()

        if (digits[index]) {
          const newDigits = digits.map((d, i) => (i === index ? '' : d))
          updateCode(newDigits)
        } else if (index > 0) {
          const newDigits = digits.map((d, i) => (i === index - 1 ? '' : d))
          updateCode(newDigits)
          focusField(index - 1)
        }
        return
      }

      if (key === 'Delete') {
        e.preventDefault()
        const newDigits = digits.map((d, i) => (i === index ? '' : d))
        updateCode(newDigits)
        return
      }

      if (key === 'ArrowLeft') {
        e.preventDefault()
        focusField(index - 1)
        return
      }

      if (key === 'ArrowRight') {
        e.preventDefault()
        focusField(index + 1)
        return
      }
    },
    [digits, disabled, length, updateCode, focusField]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return

      e.preventDefault()

      const pastedText = e.clipboardData.getData('text/plain').replace(/\D/g, '')
      const pastedDigits = pastedText.slice(0, length).split('')

      const newDigits = digits.map((d, i) => pastedDigits[i] ?? d).slice(0, length)

      updateCode(newDigits)

      const nextEmptyIndex = newDigits.findIndex((d) => !d)
      focusField(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex)
    },
    [digits, disabled, length, updateCode, focusField]
  )

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }, [])

  useEffect(() => {
    if (isCodeComplete && onComplete && !isCompleteCalledRef.current) {
      isCompleteCalledRef.current = true
      onComplete(digits.join(''))
    } else if (!isCodeComplete) {
      isCompleteCalledRef.current = false
    }
  }, [isCodeComplete, digits, onComplete])

  useEffect(() => {
    if (!disabled) {
      const firstEmptyIndex = digits.findIndex((d) => !d)
      focusField(firstEmptyIndex === -1 ? 0 : firstEmptyIndex)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const half = Math.floor(digits.length / 2) - 1

  return (
    <Box flex align='center' fullWidth justify='center' gap='x8' role='group'>
      {digits.map((digit, index) => (
        <React.Fragment key={index}>
          <Input
            type='tel'
            inputMode='numeric'
            pattern='[0-9]*'
            variant='digital'
            maxLength={1}
            autoComplete='off'
            data-testid={`verification-code-input-${index}`}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            error={error}
            disabled={disabled}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
          />
          {index === half && <span>-</span>}
        </React.Fragment>
      ))}
    </Box>
  )
}
